// Career miniapp — URL ↔ search state helper.
// SSOT a régi Jobs/Index.php URL paraméter nevekre (keyword, category, location,
// locationext, jobworktype, experience, client, maincategory).
// A backend hívás belső név más néhány mezőnél: keyword→job, location→regio, locationText→city.
const DEFAULTS = {
    keyword: '', category: 0, location: 0, locationText: '', city: '',
    jobWorkType: 0, experience: 0, experienceOther: 0, client: '', mainCategory: 0,
};
const URL_TO_STATE = {
    keyword: 'keyword',
    category: 'category',
    location: 'location',
    locationext: 'locationText',
    city: 'city',
    jobworktype: 'jobWorkType',
    experience: 'experience',
    experienceother: 'experienceOther',
    client: 'client',
    maincategory: 'mainCategory',
};
const STATE_TO_URL = {
    keyword: 'keyword',
    category: 'category',
    location: 'location',
    locationText: 'locationext',
    city: 'city',
    jobWorkType: 'jobworktype',
    experience: 'experience',
    experienceOther: 'experienceother',
    client: 'client',
    mainCategory: 'maincategory',
};
const INT_FIELDS = ['category', 'location', 'jobWorkType', 'experience', 'experienceOther', 'mainCategory'];
export function readUrlParams() {
    if (typeof window === 'undefined') {
        return { ...DEFAULTS };
    }
    const u = new URLSearchParams(window.location.search);
    const out = { ...DEFAULTS };
    Object.keys(URL_TO_STATE).forEach(urlKey => {
        const stateKey = URL_TO_STATE[urlKey];
        const raw = u.get(urlKey);
        if (raw == null)
            return;
        if (INT_FIELDS.indexOf(stateKey) !== -1) {
            const n = parseInt(raw, 10);
            out[stateKey] = isNaN(n) || n < 0 ? 0 : n;
        }
        else {
            out[stateKey] = raw;
        }
    });
    // Legacy alias: a régi Jobs/Index.php és bookmark-olt URL-ek `?regio=` paramétert
    // használhattak `?location=` helyett. Ha a kanonikus `location` nincs megadva,
    // engedjük be a `regio` értékét — különben ne írjuk felül.
    if (out.location === 0) {
        const legacyRegio = u.get('regio');
        if (legacyRegio != null) {
            const n = parseInt(legacyRegio, 10);
            if (!isNaN(n) && n > 0) {
                out.location = n;
            }
        }
    }
    return out;
}
export function pushUrlParams(partial) {
    if (typeof window === 'undefined' || !window.history?.pushState)
        return;
    const current = readUrlParams();
    const merged = { ...current, ...partial };
    const existing = new URLSearchParams(window.location.search);
    const handledKeys = new Set(Object.keys(URL_TO_STATE));
    // [B-CAREERREGIO] a legacy `regio` alias NEM őrződik meg: a kanonikus `location` hordozza az
    // értéket; különben "Minden lokáció" választása után az URL-ben maradó ?regio=N újratöltéskor
    // visszahozná a törölt szűrőt.
    handledKeys.add('regio');
    const preserved = [];
    existing.forEach((v, k) => {
        if (!handledKeys.has(k))
            preserved.push([k, v]);
    });
    const out = new URLSearchParams();
    Object.keys(STATE_TO_URL).forEach(stateKey => {
        const urlKey = STATE_TO_URL[stateKey];
        const v = merged[stateKey];
        if (INT_FIELDS.indexOf(stateKey) !== -1) {
            if (typeof v === 'number' && v > 0)
                out.set(urlKey, String(v));
        }
        else {
            if (typeof v === 'string' && v !== '')
                out.set(urlKey, v);
        }
    });
    preserved.forEach(([k, v]) => out.set(k, v));
    const qs = out.toString();
    const newUrl = window.location.protocol + '//' + window.location.host + window.location.pathname + (qs ? '?' + qs : '');
    window.history.pushState({ path: newUrl }, '', newUrl);
}
/**
 * [B-CAREERREGIO] Egyetlen lokáció-mód szabály induláskor (URL), megjelenítéskor és beküldéskor:
 *  - extended mód (ui_config.is_filters_location_extended): a lokáció mező maga a szabad-szöveges
 *    város (`locationText` → backend city); a régió-select (`location`) és a külön Város mező
 *    (`city`) NEM él - az URL-ből érkező értékük eldobva, így az első betöltés és egy változtatás
 *    nélküli beküldés ugyanazt szűri.
 *  - nem-extended mód: régió-select (`location` → regio) + opcionális Város mező (`city` → city);
 *    a `locationText` (?locationext=) nem él.
 */
export function applyLocationMode(p, isLocExtended) {
    if (isLocExtended) {
        return { ...p, location: 0, city: '' };
    }
    return { ...p, locationText: '' };
}
export function toBackendParams(p, extra) {
    const out = {};
    if (p.keyword && p.keyword.trim() !== '')
        out.job = p.keyword.trim();
    if (p.category > 0)
        out.category = p.category;
    if (p.location > 0)
        out.regio = p.location;
    // [B-CAREERCITY] a szabad-szöveges lokáció mező VÁROS-szűrő: a backend `city` paramétere
    // (job_data.city LIKE). A `locationext` a backendben a Vármegye-választó (legacy select)
    // paramétere, amit a fuzzy megye-illesztő dolgoz fel - szabad szöveggel 'Szolnok' az egész
    // megyét, 'Karcag' (nincs megye-találat) pedig MINDEN állást visszaadta (hsa, 2026-10-09).
    // Az URL-paraméter neve (`?locationext=`) változatlan (SSOT, bookmark-kompatibilitás).
    if (p.locationText && p.locationText.trim() !== '')
        out.city = p.locationText.trim();
    // [B-CAREERREGIO] a konfigurálható szabad-szöveges Város mező (filter key 'city') ugyanazt
    // a backend `city` paramétert (job_data.city LIKE) adja; ha extended módban a lokáció-szöveg
    // is ki lenne töltve, az explicit Város mező nyer (a SearchBar extended módban nem is rendereli).
    if (p.city && p.city.trim() !== '')
        out.city = p.city.trim();
    if (p.jobWorkType > 0)
        out.jobworktype = p.jobWorkType;
    if (p.experience > 0)
        out.experience = p.experience;
    if (p.experienceOther > 0)
        out.experienceother = p.experienceOther;
    if (p.client && p.client.trim() !== '')
        out.client = p.client.trim();
    if (p.mainCategory > 0)
        out.maincategory = p.mainCategory;
    if (extra)
        Object.assign(out, extra);
    return out;
}
